import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const BULLETS = [
  'Every client, deadline & service in one place',
  'Indian statutory due dates pre-loaded',
  'Role-based access — Admin, Manager, Consultant',
  'Recurring services renew automatically',
];

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      onLogin(response.data.access_token, response.data.user, response.data.organization);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── Left panel — dark ────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 flex-col bg-[#0F172A] relative overflow-hidden">

        {/* Subtle grid overlay */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="lp-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#lp-grid)" />
        </svg>

        {/* Glow blobs */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 blur-3xl"
             style={{ background: 'radial-gradient(circle, #6366F1 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10 blur-3xl"
             style={{ background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

        {/* Logo */}
        <div className="relative z-10 p-10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-gray-900 font-black text-sm">D</span>
            </div>
            <span className="font-bold text-xl text-white tracking-tight">DueDate</span>
          </div>
        </div>

        {/* Main content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-10 pb-16">

          <div className="inline-flex items-center gap-2 bg-white/10 text-gray-300 text-xs font-semibold px-4 py-2 rounded-full mb-8 tracking-wide border border-white/10 w-fit">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            Purpose-built for CA &amp; Tax Firms
          </div>

          <h2 className="text-4xl font-black text-white leading-[1.1] tracking-tight mb-5 max-w-sm">
            The command centre for practices that run on precision.
          </h2>

          <p className="text-gray-400 text-sm leading-relaxed mb-10 max-w-xs">
            Bring every client, deadline, service, and team member into one intelligent workspace.
          </p>

          <ul className="space-y-4">
            {BULLETS.map(b => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle size={15} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300 leading-snug">{b}</span>
              </li>
            ))}
          </ul>

          {/* Stats strip */}
          <div className="mt-12 grid grid-cols-3 rounded-2xl overflow-hidden border border-white/10">
            {[
              { value: '20+',       label: 'Compliance types' },
              { value: '3 roles',   label: 'Access control' },
              { value: 'Auto',      label: 'Period renewal' },
            ].map(({ value, label }) => (
              <div key={label} className="bg-white/5 px-4 py-4 text-center border-r border-white/10 last:border-0">
                <p className="text-lg font-black text-white tracking-tight">{value}</p>
                <p className="text-[10px] text-gray-500 font-medium tracking-wide mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel — form ───────────────────────────────────── */}
      <div className="w-full lg:w-1/2 flex flex-col bg-white">

        {/* Top bar */}
        <div className="flex items-center justify-between px-8 py-6">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 lg:hidden">
            <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">D</span>
            </div>
            <span className="font-bold text-gray-900 tracking-tight">DueDate</span>
          </div>
          <div className="hidden lg:block" />

          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={13} /> Back to home
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center px-8 pb-12">
          <div className="w-full max-w-md">

            <div className="mb-10">
              <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-2">
                {isLogin ? 'Welcome back.' : 'Get started.'}
              </h1>
              <p className="text-gray-500 text-sm">
                {isLogin
                  ? 'Sign in to your DueDate workspace.'
                  : 'Create your account — free for 14 days.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">

              {!isLogin && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <User size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      data-testid="name-input"
                      className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                      placeholder="Jane Smith"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    data-testid="email-input"
                    className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                    placeholder="you@yourfirm.com"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="password"
                    data-testid="password-input"
                    className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="submit-button"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white font-black py-3.5 rounded-xl hover:bg-gray-700 transition-all hover:-translate-y-0.5 text-sm tracking-wide disabled:opacity-60 disabled:translate-y-0 mt-2"
              >
                {loading
                  ? 'Please wait…'
                  : isLogin
                    ? <><span>Sign in</span> <ArrowRight size={14} /></>
                    : <><span>Create account</span> <ArrowRight size={14} /></>
                }
              </button>
            </form>

            <div className="mt-8 text-center">
              <button
                type="button"
                data-testid="toggle-auth-mode"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-gray-500 hover:text-gray-900 font-semibold transition-colors"
              >
                {isLogin
                  ? "Don't have an account? Sign up free"
                  : 'Already have an account? Sign in'}
              </button>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6 tracking-wide">
              14-day free trial &nbsp;·&nbsp; No credit card required
            </p>

          </div>
        </div>
      </div>

    </div>
  );
}
